export type OutboundMailAvailability = Readonly<{
  technically_supported: boolean;
  environment_enabled: boolean;
  mailbox_enabled: boolean;
  user_permitted: boolean;
  approval_required: boolean;
  approval_status: 'not_required' | 'missing' | 'pending' | 'approved' | 'rejected';
  operationally_available: boolean;
  reason_codes: string[];
}>;

const safeReasonCopy: Record<string, string> = {
  OUTBOUND_TECHNICALLY_UNSUPPORTED: 'The delivery service is not available for outbound email.',
  OUTBOUND_ENVIRONMENT_DISABLED: 'Outbound email is switched off in this environment.',
  OUTBOUND_MAILBOX_UNAVAILABLE: 'This mailbox is not enabled for outbound email.',
  OUTBOUND_USER_NOT_PERMITTED: 'You can read this conversation, but you do not have permission to prepare or send email.',
  OUTBOUND_MAILBOX_REAUTHORISATION_REQUIRED: 'This mailbox needs reconnecting before email can be sent.',
  OUTBOUND_MAILBOX_SCOPE_UNVERIFIED: 'This mailbox\'s access hasn\'t been verified yet.',
  OUTBOUND_MAILBOX_SENDER_NOT_AUTHORISED: 'You\'re not set up to send from this mailbox.',
  OUTBOUND_APPROVAL_MISSING: 'This controlled email needs approval before it can be sent.',
  OUTBOUND_APPROVAL_PENDING: 'This controlled email is waiting for approval.',
  OUTBOUND_APPROVAL_REJECTED: 'This draft is not approved for sending.',
  OUTBOUND_THREAD_CLOSED: 'Restore this conversation before sending an approved draft.',
  OUTBOUND_RECIPIENT_UNAVAILABLE: 'A current recipient is required before this draft can be sent.',
  OUTBOUND_DELIVERY_RECONCILIATION_REQUIRED: 'A previous delivery attempt must be checked before retrying.',
  OUTBOUND_ALREADY_SENT: 'This draft has already been sent.',
};

// Per-mailbox sending set-up, fixed in the mailbox's settings rather than by a role change.
export const mailboxSetupReasons = ['OUTBOUND_MAILBOX_REAUTHORISATION_REQUIRED', 'OUTBOUND_MAILBOX_SCOPE_UNVERIFIED', 'OUTBOUND_MAILBOX_SENDER_NOT_AUTHORISED'] as const;
const mailboxSetupReason = (availability: OutboundMailAvailability) =>
  availability.reason_codes.find(code => (mailboxSetupReasons as readonly string[]).includes(code));
const roleNotPermitted = (availability: OutboundMailAvailability) =>
  !availability.user_permitted && (availability.reason_codes.includes('OUTBOUND_USER_NOT_PERMITTED') || !mailboxSetupReason(availability));

/** True when someone who manages mailboxes can fix sending from the mailbox's settings. */
export function outboundMailboxSetupNeeded(availability: OutboundMailAvailability) {
  return !availability.operationally_available && !availability.reason_codes.includes('OUTBOUND_USER_NOT_PERMITTED') && Boolean(mailboxSetupReason(availability));
}

export function outboundMailPresentation(availability: OutboundMailAvailability) {
  if (availability.operationally_available) {
    return { label: 'Ready to send', detail: 'This email can be sent from the selected mailbox.' } as const;
  }
  const reason = availability.reason_codes[0];
  return {
    label: roleNotPermitted(availability) ? 'Read only' : reason && mailboxSetupReason(availability) === reason ? 'Sending not set up' : 'Sending unavailable',
    detail: reason ? safeReasonCopy[reason] ?? 'Outbound email is not available for this draft.'
      : 'Outbound email is not available for this draft.',
  } as const;
}

export function outboundMailboxPresentation(availability: OutboundMailAvailability) {
  if (roleNotPermitted(availability)) return { label: 'Read only', detail: safeReasonCopy.OUTBOUND_USER_NOT_PERMITTED } as const;
  if (!availability.technically_supported) return { label: 'Unavailable', detail: safeReasonCopy.OUTBOUND_TECHNICALLY_UNSUPPORTED } as const;
  if (!availability.environment_enabled) return { label: 'Switched off', detail: safeReasonCopy.OUTBOUND_ENVIRONMENT_DISABLED } as const;
  if (!availability.mailbox_enabled) return { label: 'Not enabled', detail: safeReasonCopy.OUTBOUND_MAILBOX_UNAVAILABLE } as const;
  const setup = mailboxSetupReason(availability);
  if (setup) return { label: 'Sending not set up', detail: safeReasonCopy[setup]! } as const;
  return { label: 'Ready for email', detail: 'Routine email can be sent directly; controlled messages retain approval.' } as const;
}
